import { QueryObserverOptions } from "@tanstack/react-query"

import { CreateAppointmentRequest } from "@shared/dtos/requests/appointmentRequests.dto"
import {
  AgendaSlotsResponse,
  AppointmentCreationResponse,
  PatientHisAppointmentResponse,
} from "@shared/dtos/responses/appointmentResponses.dto"
import { SupportedAppointmentTypesResponse } from "@shared/zod/schemas/responses/appointment"

import { axiosPatient } from "./generic/AxiosInterceptor/AxiosInterceptor"

/**
 * Patient requests the available slots (initial step to book an appointment)
 * @param pageId: Site page, from a practice, determining which agendas/blocks to show
 * @param platoSessionId: Optional Plato session ID for outcome-based filtering
 */
export const getAgendaSlots = (pageId: string, platoSessionId?: string): Promise<AgendaSlotsResponse[]> => {
  const baseUrl = `/patient/appointments/slots/${pageId}`
  const url = platoSessionId ? `${baseUrl}?platoSessionId=${platoSessionId}` : baseUrl

  return axiosPatient({
    url,
    method: "get",
  }).then((resp) => resp.data)
}

// The advice page withholds the alternative options while this request is in
// flight and shows all of them once it fails. The global retry backoff (1s, 3s)
// would only keep the escalation routes hidden longer, so fail fast instead.
export const getSupportedAppointmentTypesQueryOptions: Partial<QueryObserverOptions> = {
  retry: false,
}

/** @see SupportedAppointmentTypesResponse — the practice's configuration, not slot availability. */
export const getSupportedAppointmentTypes = (pageId: string): Promise<SupportedAppointmentTypesResponse> => {
  return axiosPatient({
    url: `/patient/appointments/types/${pageId}`,
    method: "get",
    // A background request on the advice page: a failure means "unknown" and
    // every option stays visible, so an unprompted toast would be the only
    // thing the patient notices about a degradation we handle ourselves.
    suppressErrorToast: true,
  }).then((resp) => resp.data)
}

export const addByPatient = (data: CreateAppointmentRequest): Promise<AppointmentCreationResponse> => {
  return axiosPatient({
    url: "/patient/appointments",
    method: "post",
    data: data,
  }).then((resp) => resp.data)
}

export const getHisAppointments = (): Promise<PatientHisAppointmentResponse[]> => {
  return axiosPatient({
    url: "/patient/hisappointments",
    method: "get",
  }).then((resp) => resp.data)
}

export const cancelHisAppointment = (hisAppointmentId: string): Promise<void> => {
  return axiosPatient({
    url: `/patient/hisappointments/${hisAppointmentId}`,
    method: "delete",
  }).then(() => undefined)
}
